import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLoadingScreen from './AuthLoadingScreen';
import styles from './ProtectedRoute.module.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute = ({
  children,
  allowedRoles = [],
}: ProtectedRouteProps): React.ReactElement => {
  const { currentUser, userRole, loading } = useAuth();

  // Show loading screen while authentication is being determined
  if (loading) {
    return <AuthLoadingScreen />;
  }

  // Redirect to login if no user is authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Show loading if user exists but role is still being determined
  // (currentUser is already guaranteed non-null by the guard above)
  if (userRole === null) {
    return <AuthLoadingScreen />;
  }

  // Check role permissions
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return (
      <div className={styles['unauthorized']}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <p>
          Please contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
